import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Row,
  Col,
  Form,
  Input,
  Button,
  Switch,
  Typography,
  Divider,
  message,
  Space,
  Avatar,
  Upload
} from 'antd';
import {
  UserOutlined,
  BellOutlined,
  SecurityScanOutlined,
  SaveOutlined,
  UploadOutlined,
  LinkOutlined,
  FormOutlined,
  FileTextOutlined,
  TeamOutlined,
  CalendarOutlined,
  ArrowRightOutlined,
  SettingOutlined,
  AudioOutlined
} from '@ant-design/icons';
import { FaHeartbeat } from 'react-icons/fa';
import BookingNotificationSettings from './BookingNotificationSettings';
import RiskScoringSettings from './RiskScoringSettings';
import VoiceSettings from '../CaseManagement/components/VoiceSettings';
import { usePageHeader } from '../../layouts/AdminLayout';
import { useStore } from '../../hooks/storeContext';
// AdminLayout now provided at router level
import { COLORS } from '../../constants/colors';
import { ROUTES } from '../../constants/routes';
import {
  PageContainer,
  SettingsCard,
  SectionTitle,
  ProfileSection,
  SettingsSection,
  SecuritySection,
  QuickLinksSection,
  StyledTabs,
  TabsBorderDiv,
  TabContent
} from './styled';

const { Title, Text } = Typography;

const Settings = observer(() => {
  const [profileForm] = Form.useForm();
  const [notificationForm] = Form.useForm();
  const [securityForm] = Form.useForm();
  const [profileLoading, setProfileLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [profileChanged, setProfileChanged] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const { authStore } = useStore();
  const { setPageHeader } = usePageHeader();

  // Set page header
  useEffect(() => {
    setPageHeader({ title: 'Settings' });
  }, [setPageHeader]);

  // Quick links data
  const quickLinks = [
    {
      title: 'Bus Screening Form',
      description: 'Mobile health screening form for field use',
      icon: <FormOutlined />,
      iconColor: '#52c41a',
      route: ROUTES.BUS_SCREENING
    },
    {
      title: 'LPA Test Request',
      description: 'Lipid Profile Assessment test request form',
      icon: <FileTextOutlined />,
      iconColor: '#1890ff',
      route: ROUTES.LPA_TEST
    },
    {
      title: 'Volunteer Registration',
      description: 'Sign up form for new volunteers',
      icon: <TeamOutlined />,
      iconColor: '#722ed1',
      route: ROUTES.VOLUNTEER
    },
    {
      title: 'Public Booking',
      description: 'Public appointment booking system',
      icon: <CalendarOutlined />,
      iconColor: '#fa8c16',
      route: ROUTES.PUBLIC_BOOKING
    }
  ];

  const handleQuickLinkClick = (route) => {
    window.open(route, '_blank');
  };

  useEffect(() => {
    // Initialize forms with user data
    if (authStore.me) {
      profileForm.setFieldsValue({
        fullName: authStore.me.fullName || '',
        email: authStore.me.email || '',
        phone: authStore.me.phone || '',
        role: authStore.me.role || 'admin'
      });
    }

    // Initialize notification settings
    notificationForm.setFieldsValue({
      emailNotifications: true,
      pushNotifications: true,
      appointmentReminders: true,
      systemAlerts: true
    });
  }, [authStore.me, profileForm, notificationForm]);

  const handleProfileUpdate = async (values) => {
    setProfileLoading(true);
    try {
      await authStore.updateUserProfile({
        fullName: values.fullName,
        email: values.email,
        phone: values.phone
      });
      setProfileChanged(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      message.error('Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleNotificationUpdate = async (values) => {
    setNotificationLoading(true);
    try {
      // TODO: Implement notification settings update API call
      console.log('Notification update values:', values);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      message.success('Notification settings updated successfully');
    } catch (error) {
      console.error('Error updating notification settings:', error);
      message.error('Failed to update notification settings');
    } finally {
      setNotificationLoading(false);
    }
  };

  const handlePasswordChange = async (values) => {
    setPasswordLoading(true);
    try {
      // TODO: Implement password change API call
      console.log('Password change values:', values);
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      
      message.success('Password changed successfully');
      securityForm.resetFields();
    } catch (error) {
      console.error('Error changing password:', error);
      message.error('Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleAvatarUpload = (info) => {
    if (info.file.status === 'done') {
      message.success(`${info.file.name} uploaded successfully`);
    } else if (info.file.status === 'error') {
      message.error(`${info.file.name} upload failed.`);
    }
  };

  const tabItems = [
    {
      key: 'basic',
      label: (
        <Space>
          <SettingOutlined />
          Basic
        </Space>
      )
    },
    {
      key: 'notifications',
      label: (
        <Space>
          <BellOutlined />
          Notifications
        </Space>
      )
    },
    {
      key: 'scoring',
      label: (
        <Space>
          <FaHeartbeat />
          Scoring
        </Space>
      )
    },
    {
      key: 'voice',
      label: (
        <Space>
          <AudioOutlined />
          Voice Agent
        </Space>
      )
    }
  ];

  // Basic Settings Content
  const renderBasicSettings = () => (
    <Row gutter={[24, 24]}>
      {/* Profile Settings */}
      <Col xs={24} lg={12}>
        <SettingsCard>
          <SectionTitle>
            <UserOutlined style={{ color: COLORS.$primaryBlue }} />
            Profile Information
          </SectionTitle>
          
          <ProfileSection>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <Avatar 
                size={80} 
                style={{ backgroundColor: COLORS.$mainRed }}
                icon={<UserOutlined />}
              />
              <div>
                <Upload
                  name="avatar"
                  showUploadList={false}
                  onChange={handleAvatarUpload}
                >
                  <Button icon={<UploadOutlined />} size="small">
                    Change Photo
                  </Button>
                </Upload>
                <Text type="secondary" style={{ display: 'block', marginTop: '4px', fontSize: '12px' }}>
                  JPG, PNG up to 2MB
                </Text>
              </div>
            </div>

            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleProfileUpdate}
              onValuesChange={() => setProfileChanged(true)}
            >
              <Form.Item
                name="fullName"
                label="Full Name"
                rules={[{ required: true, message: 'Please enter your full name' }]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="email"
                label="Email Address"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                name="phone"
                label="Phone Number"
              >
                <Input placeholder="Enter phone number" />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={profileLoading}
                  disabled={!profileChanged}
                  icon={<SaveOutlined />}
                >
                  Save
                </Button>
              </div>
            </Form>
          </ProfileSection>
        </SettingsCard>
      </Col>

      {/* Security Settings */}
      <Col xs={24} lg={12}>
        <SettingsCard>
          <SectionTitle>
            <SecurityScanOutlined style={{ color: COLORS.$primaryBlue }} />
            Security Settings
          </SectionTitle>
          
          <SecuritySection>
            <Form
              form={securityForm}
              layout="vertical"
              onFinish={handlePasswordChange}
            >
              <Title level={5}>Change Password</Title>
              
              <Form.Item
                name="currentPassword"
                label="Current Password"
                rules={[{ required: true, message: 'Please enter your current password' }]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                name="newPassword"
                label="New Password"
                rules={[
                  { required: true, message: 'Please enter a new password' },
                  { min: 8, message: 'Password must be at least 8 characters long' }
                ]}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirm New Password"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Please confirm your new password' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('Passwords do not match'));
                    },
                  }),
                ]}
                style={{ marginBottom: '24px' }}
              >
                <Input.Password />
              </Form.Item>

              <Form.Item style={{ marginBottom: '16px' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={passwordLoading}
                  icon={<SaveOutlined />}
                >
                  Change Password
                </Button>
              </Form.Item>
            </Form>
            
            <div className="security-info-box" style={{ marginTop: '16px' }}>
              <Text strong>Password Requirements:</Text>
              <ul>
                <li>At least 8 characters long</li>
                <li>Include uppercase and lowercase letters</li>
                <li>Include at least one number</li>
                <li>Include at least one special character</li>
              </ul>
            </div>
          </SecuritySection>
        </SettingsCard>
      </Col>

      {/* Quick Links */}
      <Col xs={24}>
        <SettingsCard>
          <SectionTitle>
            <LinkOutlined style={{ color: COLORS.$primaryBlue }} />
            Quick Access Links
          </SectionTitle>
          
          <QuickLinksSection>
            <Text type="secondary" style={{ display: 'block', marginBottom: '16px' }}>
              Quick access to all application forms and public pages. Links will open in a new tab.
            </Text>
            
            <div className="quick-links-grid">
              {quickLinks.map((link, index) => (
                <div 
                  key={index}
                  className="quick-link-item"
                  onClick={() => handleQuickLinkClick(link.route)}
                >
                  <div 
                    className="link-icon" 
                    style={{ backgroundColor: link.iconColor }}
                  >
                    {link.icon}
                  </div>
                  <div className="link-content">
                    <div className="link-title">{link.title}</div>
                    <div className="link-description">{link.description}</div>
                  </div>
                  <ArrowRightOutlined className="link-arrow" />
                </div>
              ))}
            </div>
          </QuickLinksSection>
        </SettingsCard>
      </Col>
    </Row>
  );

  // Notifications Settings Content
  const renderNotificationsSettings = () => (
    <Row gutter={[24, 24]}>
      {/* Personal Notification Preferences */}
      <Col xs={24} lg={12}>
        <SettingsCard>
          <SectionTitle>
            <BellOutlined style={{ color: COLORS.$primaryBlue }} />
            Personal Notification Preferences
          </SectionTitle>
          
          <SettingsSection>
            <Form
              form={notificationForm}
              layout="vertical"
              onFinish={handleNotificationUpdate}
            >
              <Form.Item
                name="emailNotifications"
                valuePropName="checked"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Email Notifications</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Receive notifications via email
                    </Text>
                  </div>
                  <Switch />
                </div>
              </Form.Item>

              <Divider style={{ margin: '16px 0' }} />

              <Form.Item
                name="pushNotifications"
                valuePropName="checked"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Push Notifications</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Receive browser push notifications
                    </Text>
                  </div>
                  <Switch />
                </div>
              </Form.Item>

              <Divider style={{ margin: '16px 0' }} />

              <Form.Item
                name="appointmentReminders"
                valuePropName="checked"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>Appointment Reminders</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Get notified about upcoming appointments
                    </Text>
                  </div>
                  <Switch />
                </div>
              </Form.Item>

              <Divider style={{ margin: '16px 0' }} />

              <Form.Item
                name="systemAlerts"
                valuePropName="checked"
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <Text strong>System Alerts</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      Important system updates and alerts
                    </Text>
                  </div>
                  <Switch />
                </div>
              </Form.Item>

              <Form.Item style={{ marginTop: '24px' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={notificationLoading}
                  icon={<SaveOutlined />}
                >
                  Save Notification Settings
                </Button>
              </Form.Item>
            </Form>
          </SettingsSection>
        </SettingsCard>
      </Col>

      {/* Booking Notification Settings */}
      <Col xs={24} lg={12}>
        <SettingsCard>
          <SectionTitle>
            <BellOutlined style={{ color: COLORS.$primaryBlue }} />
            Booking Notification Settings
          </SectionTitle>
          <BookingNotificationSettings />
        </SettingsCard>
      </Col>
    </Row>
  );

  // Risk Scoring Settings Content
  const renderScoringSettings = () => (
    <Row gutter={[24, 24]}>
      <Col xs={24}>
        <SettingsCard>
          <SectionTitle>
            <FaHeartbeat style={{ color: COLORS.$primaryBlue }} />
            Cardiovascular Risk Score Methodology
          </SectionTitle>
          <RiskScoringSettings />
        </SettingsCard>
      </Col>
    </Row>
  );

  // Voice Agent Settings Content
  const renderVoiceSettings = () => (
    <div style={{ marginTop: 8 }}>
      <VoiceSettings />
    </div>
  );

  return (
    <PageContainer>
      <StyledTabs 
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ marginTop: 0, marginBottom: 0 }}
        size="large"
      />
      <TabsBorderDiv />

      <TabContent>
        {activeTab === 'basic' && renderBasicSettings()}
        {activeTab === 'notifications' && renderNotificationsSettings()}
        {activeTab === 'scoring' && renderScoringSettings()}
        {activeTab === 'voice' && renderVoiceSettings()}
      </TabContent>
    </PageContainer>
  );
});

export default Settings;
